// Dashboard logout helper.
//
// Phase 7+ (native auth) flow — replaces the legacy CF-Access path.
//
// Responsibilities:
//   1. Call POST /v1/auth/logout on the API. The server revokes the
//      nexus_refresh row in nexus_control.refresh_tokens and emits Set-Cookie
//      headers expiring nexus_session, nexus_refresh, and nexus_session_present
//      (Domain=.nexus.sentry.red, Max-Age=0). Server-side cookie
//      wipe is the source of truth — JS cannot reach the httpOnly ones.
//   2. Belt-and-braces, JS-side wipe local browser state:
//        - localStorage["location.dashboard.session"] (DashboardSession)
//        - localStorage["location.dashboard.jwt"]     (legacy mock JWT)
//        - sessionStorage (defensive — debug/QA flows occasionally)
//        - non-httpOnly cookies on the current origin (nexus_session_present
//          if the API call somehow didn't reach us; future hint cookies)
//   3. Navigate to /login. Not / — the user clicked "登出", they expect
//      to land on the sign-in surface, not the marketing page.
//
// The returned function never resolves — it triggers a full-page
// navigation once the API call settles (we tolerate failures because
// the local wipe + navigation is what the user perceives as "logged
// out", and the cookies are HttpOnly+Secure so a stale browser tab
// can't keep using a revoked session for long anyway).

import { apiFetch } from "../../lib/api-client.js";

const SESSION_KEY = "location.dashboard.session";
const JWT_KEY = "location.dashboard.jwt";

/** Clear local browser state. Exported for tests + manual recovery. */
export function clearLocalState(): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(JWT_KEY);
    }
  } catch { /* private mode / disabled storage */ }

  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.clear();
    }
  } catch { /* same */ }

  // Best-effort cookie wipe (only non-HttpOnly cookies are reachable from
  // JS; nexus_session / nexus_refresh are HttpOnly so the server's Set-Cookie
  // Max-Age=0 in /v1/auth/logout is what actually drops them).
  try {
    if (typeof document !== "undefined" && typeof document.cookie === "string") {
      const cookies = document.cookie.split(";");
      const expire = "Thu, 01 Jan 1970 00:00:01 GMT";
      for (const c of cookies) {
        const eq = c.indexOf("=");
        const name = (eq > -1 ? c.slice(0, eq) : c).trim();
        if (!name) continue;
        // Cover bare host + dotted (`.nexus.sentry.red`) variants.
        document.cookie = `${name}=; expires=${expire}; path=/`;
        document.cookie = `${name}=; expires=${expire}; path=/dashboard`;
        document.cookie = `${name}=; expires=${expire}; path=/; domain=.nexus.sentry.red`;
      }
    }
  } catch { /* ignore */ }
}

/** Hit the logout endpoint. Resolves regardless of HTTP status — the
 *  client-side wipe + redirect is what matters for UX. */
export async function callLogoutApi(): Promise<void> {
  try {
    await apiFetch("/v1/auth/logout", {
      method: "POST",
      credentials: "include",
    });
  } catch (err) {
    // Network failure is fine — the server is unreachable, but the
    // browser-side wipe still runs and the user lands on /login. The
    // session cookie will expire on its own (15 min TTL).
    // eslint-disable-next-line no-console
    console.warn("[logout] /v1/auth/logout call failed:", (err as Error)?.message ?? err);
  }
}

/** Append `logged_out=1` to a /login destination (preserving any existing
 *  query such as return_to). LoginPage reads this marker to show a manual
 *  "sign in" button instead of auto-restarting the Accounts OIDC flow — an
 *  immediate redirect against a still-live IdP session would silently re-mint
 *  a session and defeat sign-out / account switching (unified login, SEN-634). */
export function withLoggedOutMarker(redirectTo: string): string {
  if (/(^|[?&])logged_out=/.test(redirectTo)) return redirectTo;
  const [path, hash = ""] = redirectTo.split("#");
  const joiner = path.includes("?") ? "&" : "?";
  const withMarker = `${path}${joiner}logged_out=1`;
  return hash ? `${withMarker}#${hash}` : withMarker;
}

/**
 * Fully log the dashboard user out: revoke server-side, wipe local
 * state, redirect to /login (tagged logged_out=1 — see withLoggedOutMarker).
 *
 * `redirectTo` defaults to "/login" — callers can pass an override
 * (e.g. "/login?return_to=/dashboard/" if they want a deep link back).
 */
export async function performLogout(redirectTo: string = "/login"): Promise<void> {
  // Order matters: API first so the server can read the cookies
  // before we wipe them locally; then client wipe; then navigate.
  await callLogoutApi();
  clearLocalState();
  if (typeof window !== "undefined") {
    window.location.assign(withLoggedOutMarker(redirectTo));
  }
}

/** React hook flavour for components that prefer a callable. */
export function useLogout(redirectTo: string = "/login"): () => void {
  return () => {
    void performLogout(redirectTo);
  };
}
